import React, { useEffect, useCallback, useMemo, useState } from "react";
import type {
  BoxScoreProps,
  BoxScoreDateType,
  BoxScoreVariantType,
  GameStateType,
  BoxScoreSyncMapperType,
} from "../types";
import cx from "components/src/utils/cx";
import { StartsIn } from "./StartsIn";
import { Countdown } from "../../Countdown/Countdown";
import { DateDetails } from "components/src/components/DateDetails/DateDetails";
import { Icon } from "components/src/components/Icon/Icon";
import { LiveStreamIcon } from "./LiveStreamIcon";
import { EventDetails } from "components-sbk/src/components/MarketTemplate/components/MarketFooter/components/EventDetails";
import type { MarketTemplateEventDetailsType } from "components-sbk/src/components/MarketTemplate/types";
import pick from "lodash-es/pick";
import { getCompetitionInfo } from "shared/src/hooks/getCompetitionInfo";

const boxScoreSyncMapper = (data) => {
  return pick(data, ["gameState", "gameTime"]);
};

const LiveDetails = ({
  eventDetails = { gameState: "---", gameTime: "---" },
  minimal,
  syncMapper,
}: {
  eventDetails?: MarketTemplateEventDetailsType;
  syncMapper?: BoxScoreSyncMapperType;
  minimal?: boolean;
}) => {
  // Manipulate passed in `eventDetails` for further customization.  Different from the 'syncMapper'
  const _eventDetails = useMemo(() => {
    return pick(eventDetails, [
      "gameState",
      "gameTime",
      "competitor1",
      "competitor2",
      "sync",
    ]);
  }, [eventDetails]);

  const classes = {
    wrapper: {
      minimal: "cui-flex cui-flex-col cui-ring-1 cui-ring-highlight-accent cui-rounded-sm",
      scoreStacked: ""
    },
    gameState: {
      minimal: "cui-flex cui-justify-center cui-bg-highlight-accent cui-text-highlight-accent-minimal display-xs-bold cui-p-2xs",
      scoreStacked: "cui-flex cui-justify-center cui-text-highlight-accent display-xs-bold"
    }, 
    evenDetailsWrapper: {
      minimal: "cui-text-highlight-accent cui-px-2xs cui-py-2px display-xs",
      scoreStacked: "cui-text-fg-inverse cui-px-2xs cui-py-2px display-xs"
    }
  }
  return (
    <div className={classes.wrapper[minimal ? "minimal" : "scoreStacked"]}>
      <div className={classes.gameState[minimal ? "minimal" : "scoreStacked"]}>
        Live
      </div>
      <div className="cui-w-full cui-h-1px cui-bg-fg-subtle" />
      <div className={classes.evenDetailsWrapper[minimal ? "minimal" : "scoreStacked"]}>
        <EventDetails
          {..._eventDetails}
          noMinHeight={true}
          noPad={true}
          textSize={"display-xs"}
          noWrap={true}
          dividerColor={!minimal ? "cui-bg-fg-global-white" : "cui-bg-highlight-accent-subtle"}
          fgColor={!minimal ? "fg-global-white" : ""}
          syncMapper={syncMapper ? syncMapper : boxScoreSyncMapper}
        />
      </div>
    </div>
  );
};

const sportsIconMap = {
  baseball: "sports_baseball",
  football: "sports_football",
  hockey: "sports_hockey",
  lacrosse: "sports_lacrosse",
  soccer: "sports_soccer",
  tennis: "sports_tennis",
  volleyball: "sports_volleyball",
  "american-football": "sports_football",
  "basketball": "sports_basketball",
  "ice-hockey": "sports_hockey",
  "rugby-league": "sports_rugby",
  "rugby-union": "sports_rugby",
  "field-hockey": "sports_hockey",
  "wrestling": "sports_martial_arts",
  "boxing": "sports_boxing",
  "golf": "sports_golf",
  "cricket": "sports_cricket",
  "water-polo": "sports_water_polo",
  "table-tennis": "sports_table_tennis",
  "badminton": "sports_badminton",
  "darts": "sports_darts",
  "futsal": "sports_futsal",
  "handball": "sports_handball",
  "floorball": "sports_floorball",
  "fishing": "sports_fishing",
}
const bigLeagues = ["nhl", "mlb", "nfl", "nba", "wnba"];

export const BoxScoreDetails = ({
  date,
  hasLiveStream,
  variant,
  showCountdown,
  gameState,
  eventDetails,
  _competition,
  competitionIcon,
  minimal,
  sport,
  syncMapper,
}: {
  date: BoxScoreDateType;
  hasLiveStream?: boolean;
  variant: BoxScoreVariantType;
  showCountdown?: boolean;
  gameState: GameStateType;
  eventDetails?: MarketTemplateEventDetailsType;
  minimal?: boolean;
  _competition?: string;
  competitionIcon?: string;
  sport?: string;
  syncMapper?: BoxScoreSyncMapperType;
}) => {
  const leagueInfo = getCompetitionInfo(_competition);

  const [showStartsIn, setShowStartsIn] = useState(false);
  const handleCountdownStop = useCallback(() => {
    setShowStartsIn(true);
  }, []);

  const _Details = useMemo(() => {
    return gameState === "live" ? (
      <LiveDetails eventDetails={eventDetails} syncMapper={syncMapper} minimal={minimal || variant === "vertical"} />
    ) : (
      <DateDetails
        date={date}
        variant={minimal || variant === "vertical" ? "outlined" : "simple"}
        orientation={minimal ? "horizontal" : "vertical"}
      />
    );
  }, [variant, date, minimal, gameState, eventDetails]);

  const isPreGame = gameState === "pregame";

  const _sportIcon = _competition && !bigLeagues.includes(_competition) && typeof sport === "string" && sport ? sportsIconMap[sport] : "ui_stadium";
  const isVertical = variant === "vertical";

  return (
    <div
      className={cx(
        {
          "cui-min-h-[50px]": isVertical,
          "cui-flex-col": isVertical && !isPreGame,
          "cui-flex-col cui-min-h-[58px]": variant === "horizontal",
          "-cui-mt-2": Boolean(hasLiveStream) && isPreGame && !minimal,
          "-cui-mt-4": Boolean(hasLiveStream) && gameState === "live" && !minimal && !isVertical
        },
        "cui-flex cui-items-center cui-justify-center cui-pointer-events-none cui-gap-0 cui-relative"
        // cui-gap-2xs
      )}
    >
      {isVertical ? (
        <>
          <div className="cui-flex cui-flex-col cui-pr-xs cui-items-end">
            {showCountdown ? (
              <>
                <div className="cui-flex cui-flex-col cui-items-end">
                  <StartsIn date={date} hide={showStartsIn} />
                  <Countdown
                    from={Date.now()}
                    stopAt={date}
                    onStop={handleCountdownStop}
                    className="-cui-mr-2px"
                  />
                </div>
                {hasLiveStream && isPreGame ? <LiveStreamIcon minimal={minimal || isVertical} /> : null}
              </>
            ) : null}
          </div>
          {_Details}
        </>
      ) : (
        <>
          {showCountdown ? (
            <div className="cui-flex cui-flex-wrap sbk-phone:cui-flex-nowrap cui-text-center cui-justify-center cui-items-center">
              <StartsIn
                date={date}
                className="cui-pr-2xs"
                hide={showStartsIn}
              />
              <Countdown
                from={Date.now()}
                stopAt={date}
                onStop={handleCountdownStop}
              />
            </div>
          ) : null}
          {!minimal && !isVertical ? <div className="cui-flex cui-items-end">
            <Icon
              name={
                leagueInfo?.metadata?.images?.primary?.name ??
                _sportIcon ??
                competitionIcon ??
                "ui_stadium"
              }
              size="lg"
            />
          </div> : null}
          <div className="cui-text-center">{_Details}</div>
          {hasLiveStream ? (
            <LiveStreamIcon minimal={minimal && !isVertical} text={gameState === "live" ? "Watch" : undefined} />
          ) : null}
        </>
      )}
    </div>
  );
};
